import { useMemo, useState } from 'react'
import { useAdminModeration, useAdminResolveFlag } from '../../hooks/useAdmin'
import { DataTable } from '../../components/admin/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import type { ContentFlag, ContentFlagStatus } from '@fellowship/shared'

function fmt(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

const TYPE_LABELS: Record<string, string> = {
  event: 'Event',
  user_bio: 'User bio',
  church: 'Church',
  image: 'Image',
}

const STATUS_TABS: { value: ContentFlagStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'dismissed', label: 'Dismissed' },
]

const STATUS_STYLE: Record<ContentFlagStatus, { bg: string; color: string }> = {
  pending: { bg: '#fef3c7', color: '#b45309' },
  confirmed: { bg: '#fee2e2', color: '#b91c1c' },
  dismissed: { bg: '#f0fdf4', color: '#166534' },
}

export default function AdminModerationPage() {
  const [status, setStatus] = useState<ContentFlagStatus>('pending')
  const { data, isLoading } = useAdminModeration({ page: 1, status })
  const resolve = useAdminResolveFlag()
  const flags = useMemo(() => data?.data ?? [], [data])

  const columns = useMemo<ColumnDef<ContentFlag, unknown>[]>(() => [
    {
      accessorKey: 'contentType',
      header: 'Type',
      meta: { label: 'Type' },
      cell: ({ getValue }) => {
        const t = getValue() as string
        return (
          <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-blue-50 text-blue-700">
            {TYPE_LABELS[t] ?? t}
          </span>
        )
      },
    },
    {
      accessorKey: 'field',
      header: 'Field',
      meta: { label: 'Field' },
      cell: ({ getValue }) => <span className="text-muted text-[12px]">{getValue() as string}</span>,
    },
    {
      accessorKey: 'flaggedValue',
      header: 'Flagged content',
      meta: { label: 'Flagged content' },
      cell: ({ row }) => {
        const f = row.original
        if (f.source === 'rekognition') {
          return (
            <a href={f.flaggedValue} target="_blank" rel="noopener noreferrer" className="text-primary no-underline hover:underline break-all text-[12px]">
              View image ↗
            </a>
          )
        }
        return (
          <span className="text-ink text-[12px] break-words line-clamp-2 max-w-[360px] inline-block">
            {f.flaggedValue}
          </span>
        )
      },
    },
    {
      id: 'categories',
      header: 'Categories',
      enableSorting: false,
      meta: { label: 'Categories' },
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1 max-w-[220px]">
          {row.original.categories.map((c) => (
            <span key={c.label} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-s2 text-ink-2">
              {c.label} {(c.score * 100).toFixed(0)}%
            </span>
          ))}
        </div>
      ),
    },
    {
      accessorKey: 'source',
      header: 'Source',
      meta: { label: 'Source' },
      cell: ({ getValue }) => <span className="text-muted text-[11px]">{getValue() as string}</span>,
    },
    {
      accessorKey: 'createdAt',
      header: 'Flagged',
      meta: { label: 'Flagged' },
      cell: ({ getValue }) => <span className="text-muted text-[12px]">{fmt(getValue() as string)}</span>,
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      meta: { label: 'Status' },
      cell: ({ getValue }) => {
        const s = getValue() as ContentFlagStatus
        const style = STATUS_STYLE[s]
        return (
          <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold" style={{ background: style.bg, color: style.color }}>
            {s}
          </span>
        )
      },
    },
    {
      id: '_actions',
      header: '',
      enableSorting: false,
      enableColumnFilter: false,
      meta: { skipExport: true },
      cell: ({ row }) => {
        const f = row.original
        if (f.status !== 'pending') {
          return <span className="text-muted text-[11px]">Resolved</span>
        }
        return (
          <div className="flex gap-2">
            <button
              onClick={() => resolve.mutate({ id: f.id, action: 'confirm' })}
              disabled={resolve.isPending}
              className="px-2.5 py-1 rounded-md text-[12px] font-medium border border-danger-line bg-danger-soft text-danger-deep disabled:opacity-50"
            >
              Confirm
            </button>
            <button
              onClick={() => resolve.mutate({ id: f.id, action: 'dismiss' })}
              disabled={resolve.isPending}
              className="px-2.5 py-1 rounded-md text-[12px] font-medium border border-line-soft bg-s2 text-ink disabled:opacity-50"
            >
              Dismiss
            </button>
          </div>
        )
      },
    },
  ], [resolve])

  return (
    <div>
      <div className="mb-6">
        <h1 className="m-0 text-[24px] font-bold">Moderation</h1>
        <p className="text-[13px] text-muted mt-2 m-0 max-w-[560px] leading-[1.5]">
          Auto-flagged content. Text is scanned asynchronously (OpenAI); images are hard-blocked on
          upload (Rekognition) and shown here for the record. Confirm to keep flagged, or dismiss
          false positives.
        </p>
      </div>

      <div className="flex gap-2 mb-4">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatus(tab.value)}
            className={`px-3 py-1.5 rounded-md text-[13px] font-medium border ${
              status === tab.value
                ? 'border-primary bg-primary text-white'
                : 'border-line-soft bg-s2 text-ink'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <DataTable
        columns={columns}
        data={flags}
        isLoading={isLoading}
        filterable
        exportable
        exportFilename="moderation"
        emptyMessage={`No ${status} content.`}
        pageSize={50}
      />
    </div>
  )
}
